import { Ant, AntRole, AntTask, AntNameGenerator, AntStats, PreviousRole, TaskThresholds, generateTaskThresholds, Position } from '../Ant';
import { Rng } from '../../Rng';

export type { PreviousRole };

/**
 * An ant, from this system's point of view.
 *
 * `previousRoles` and `lastTransitionTime` used to be declared here as optional
 * additions and bolted onto ants at transition time. They are now fields on `Ant`,
 * because they are state: the history decides a transitioned ant's name, and the
 * timestamp decides whether it may transition again. A snapshot cannot carry what
 * the type system does not admit exists, and this pair was invisible until one
 * tried to.
 *
 * The name is kept because the casts that use it read well, and because a system
 * naming the shape it needs is worth more than one less line.
 */
export type TransitionableAnt = Ant;

export interface RoleTransitionConfig {
  TRANSITION_CHECK_INTERVAL: number;
  CRITICAL_FORAGER_RATIO: number;
  CRITICAL_NURSE_RATIO: number;
  CRITICAL_SOLDIER_RATIO: number;
  TRANSITION_COOLDOWN: number;
  MIN_COLONY_SIZE: number;
}

export const DEFAULT_TRANSITION_CONFIG: RoleTransitionConfig = {
  TRANSITION_CHECK_INTERVAL: 30000,
  CRITICAL_FORAGER_RATIO: 0.25,
  CRITICAL_NURSE_RATIO: 0.15,
  CRITICAL_SOLDIER_RATIO: 0.10,
  TRANSITION_COOLDOWN: 60000,
  MIN_COLONY_SIZE: 20,
};

export interface RoleTransitionContext {
  ants: Map<number, Ant>;
  antsOnSurface: Set<number>;
  gameTimeMs: number;
  alertState: boolean;
  getForagingUrgency: () => number;
  getHungryLarvaeCount: () => number;
  addNews: (message: string, type: 'info' | 'warning' | 'alert' | 'death') => void;
  // Local observation functions
  getChamberAt: (pos: Position) => { stimuli: { broodHunger: number; foodLow: number; alarmLevel: number }; type: string } | null;
  getAntsNearby: (pos: Position, radius: number) => Ant[];
}

/**
 * Allowed role transitions (biologically constrained).
 */
const ALLOWED_TRANSITIONS: Map<AntRole, AntRole[]> = new Map([
  [AntRole.NURSE, [AntRole.FORAGER]],
  [AntRole.FORAGER, [AntRole.NURSE, AntRole.SOLDIER]],
  [AntRole.ENGINEER, [AntRole.SOLDIER]],
  // Soldiers don't typically transition (terminal role)
  // Queen never transitions
]);

/**
 * Manages dynamic role transitions for ants based on colony needs.
 */
export class RoleTransitionSystem {
  private config: RoleTransitionConfig;
  private lastCheckTime = 0;
  private rng: Rng;

  constructor(rng: Rng, config: Partial<RoleTransitionConfig> = {}) {
    this.rng = rng;
    this.config = { ...DEFAULT_TRANSITION_CONFIG, ...config };
  }

  /**
   * When roles were last reconsidered, in colony sim-time.
   *
   * Part of a snapshot: a colony restored with this zeroed would hold its next
   * transition check for a full interval, and one that had just run one would
   * hold it twice. The generator is not saved here — it is the colony's own
   * stream, shared by reference, and the colony saves it once.
   */
  save(): number {
    return this.lastCheckTime;
  }

  load(lastCheckTime: number): void {
    this.lastCheckTime = lastCheckTime;
  }

  /**
   * Check and execute role transitions based on LOCAL observations.
   * Each ant makes individual decisions based on:
   * - Chamber pheromones (stimuli) it's currently sensing
   * - Nearby ants it can observe
   * - Random chance (not all ants consider transition at once)
   *
   * Returns array of ants that transitioned.
   */
  update(dt: number, context: RoleTransitionContext): TransitionableAnt[] {
    // Only check periodically (but more frequently for responsiveness)
    if (context.gameTimeMs - this.lastCheckTime < this.config.TRANSITION_CHECK_INTERVAL) {
      return [];
    }
    this.lastCheckTime = context.gameTimeMs;

    const transitioned: TransitionableAnt[] = [];

    // Each ant individually considers transition based on local conditions
    for (const ant of context.ants.values()) {
      if (!ant.isAlive()) continue;
      if (context.antsOnSurface.has(ant.id)) continue;
      if (ant.role === AntRole.QUEEN) continue;

      // Check individual cooldown
      const transAnt = ant as TransitionableAnt;
      if (transAnt.lastTransitionTime &&
          context.gameTimeMs - transAnt.lastTransitionTime < this.config.TRANSITION_COOLDOWN) {
        continue;
      }

      // Only small random chance to consider transition each check
      // This creates natural stochastic behavior
      if (this.rng.next() > 0.1) continue; // 10% chance per check interval

      // Get local observations
      const chamber = context.getChamberAt(ant.position);
      const nearbyAnts = context.getAntsNearby(ant.position, 100);

      // Count nearby ants by role
      const nearbyNurses = nearbyAnts.filter(a => a.role === AntRole.NURSE).length;
      const nearbyForagers = nearbyAnts.filter(a => a.role === AntRole.FORAGER).length;
      const nearbySoldiers = nearbyAnts.filter(a => a.role === AntRole.SOLDIER).length;

      // Local stimuli (pheromone-like signals)
      const broodHunger = chamber?.stimuli.broodHunger ?? 0;
      const foodLow = chamber?.stimuli.foodLow ?? 0;
      const alarmLevel = chamber?.stimuli.alarmLevel ?? 0;

      let newRole: AntRole | null = null;

      // FORAGER considering transition
      if (ant.role === AntRole.FORAGER) {
        // Forager → Nurse: Sensing strong brood hunger + few nurses nearby
        // "I smell hungry larvae but no one is caring for them"
        if (broodHunger > 0.4 && nearbyNurses < 2) {
          // Higher hunger = higher transition probability
          const transitionProb = broodHunger * 0.3;
          if (this.rng.chance(transitionProb)) {
            newRole = AntRole.NURSE;
          }
        }
        // Forager → Soldier: Strong alarm + few soldiers nearby
        // "Danger! But where are the soldiers?"
        else if (alarmLevel > 0.5 && nearbySoldiers < 2) {
          const transitionProb = alarmLevel * 0.2;
          if (this.rng.chance(transitionProb)) {
            newRole = AntRole.SOLDIER;
          }
        }
      }
      // NURSE considering transition
      else if (ant.role === AntRole.NURSE) {
        // Nurse → Forager: Low brood hunger + strong food shortage + few foragers
        // "Larvae are fed but colony is starving - I should help gather food"
        if (broodHunger < 0.2 && foodLow > 0.5 && nearbyForagers < 2) {
          const transitionProb = foodLow * 0.25;
          if (this.rng.chance(transitionProb)) {
            newRole = AntRole.FORAGER;
          }
        }
      }
      // ENGINEER considering transition
      else if (ant.role === AntRole.ENGINEER) {
        // Engineer → Soldier: Strong alarm + few soldiers
        if (alarmLevel > 0.6 && nearbySoldiers < 2) {
          const transitionProb = alarmLevel * 0.15;
          if (this.rng.chance(transitionProb)) {
            newRole = AntRole.SOLDIER;
          }
        }
      }

      // Execute transition if decided
      if (newRole !== null) {
        this.executeTransition(transAnt, newRole, context);
        transitioned.push(transAnt);
      }
    }

    return transitioned;
  }

  /**
   * Count ants by role (excluding queen and ants on surface).
   */
  private countRoles(context: RoleTransitionContext): {
    forager: number;
    nurse: number;
    soldier: number;
    engineer: number;
  } {
    const counts = { forager: 0, nurse: 0, soldier: 0, engineer: 0 };

    for (const ant of context.ants.values()) {
      if (!ant.isAlive()) continue;
      if (context.antsOnSurface.has(ant.id)) continue;

      switch (ant.role) {
        case AntRole.FORAGER:
          counts.forager++;
          break;
        case AntRole.NURSE:
          counts.nurse++;
          break;
        case AntRole.SOLDIER:
          counts.soldier++;
          break;
        case AntRole.ENGINEER:
          counts.engineer++;
          break;
      }
    }

    return counts;
  }

  /**
   * Find a suitable candidate for role transition.
   */
  private findTransitionCandidate(
    context: RoleTransitionContext,
    fromRole: AntRole,
    toRole: AntRole
  ): TransitionableAnt | null {
    // Verify this transition is allowed
    const allowed = ALLOWED_TRANSITIONS.get(fromRole);
    if (!allowed || !allowed.includes(toRole)) {
      return null;
    }

    const candidates: TransitionableAnt[] = [];

    for (const ant of context.ants.values()) {
      if (!ant.isAlive()) continue;
      if (ant.role !== fromRole) continue;
      if (context.antsOnSurface.has(ant.id)) continue;

      const transAnt = ant as TransitionableAnt;

      // Check transition cooldown. Null means never transitioned, which is what
      // `undefined` meant when this field was bolted on at transition time — the
      // check is skipped rather than measured against zero, or nothing could
      // transition inside the first cooldown of a world's life.
      if (transAnt.lastTransitionTime != null) {
        if (context.gameTimeMs - transAnt.lastTransitionTime < this.config.TRANSITION_COOLDOWN) {
          continue;
        }
      }

      // Skip ants that are busy with critical tasks
      if (this.isBusyWithCriticalTask(ant)) {
        continue;
      }

      candidates.push(transAnt);
    }

    if (candidates.length === 0) return null;

    // Prefer older ants for transitions (they've contributed to their current role)
    candidates.sort((a, b) => b.age - a.age);

    // Return the oldest suitable candidate
    return candidates[0];
  }

  /**
   * Check if ant is busy with a task that shouldn't be interrupted.
   */
  private isBusyWithCriticalTask(ant: Ant): boolean {
    switch (ant.task) {
      case AntTask.CARRYING_EGG:
      case AntTask.CARRYING_FOOD:
      case AntTask.REPAIRING:
      case AntTask.RESPONDING_ALARM:
      case AntTask.TRANSITIONING:
        return true;
      default:
        return false;
    }
  }

  /**
   * Execute a role transition for an ant.
   */
  private executeTransition(
    ant: TransitionableAnt,
    newRole: AntRole,
    context: RoleTransitionContext
  ): void {
    const oldRole = ant.role;
    const oldName = ant.name;

    // Store previous role info
    ant.previousRoles.push({
      role: oldRole,
      name: oldName,
      age: ant.age,
      stats: { ...ant.stats },
      transitionTime: context.gameTimeMs,
    });

    // Generate new name
    const newName = this.generateTransitionName(oldName, newRole, ant.previousRoles.length);

    // Update the ant
    // Note: Since role is readonly, we need to do some TypeScript magic
    // In practice, this works because we're modifying the same object
    (ant as any).role = newRole;
    ant.name = newName;
    ant.customName = null; // Clear custom name on transition

    // Update thresholds for new role
    ant.taskThresholds = generateTaskThresholds(newRole, this.rng);

    // Update physical attributes for new role
    this.updatePhysicalAttributes(ant, newRole);

    // Reset task
    ant.task = AntTask.NONE;
    ant.taskTimer = 0;
    ant.carryingFood = false;
    ant.carryingEgg = false;
    ant.carriedFoodAmount = 0;

    // Set transition cooldown
    ant.lastTransitionTime = context.gameTimeMs;

    // Announce the transition
    context.addNews(
      `${oldName} has become ${newName} (${oldRole} → ${newRole})`,
      'info'
    );
  }

  /**
   * Generate a new name for a transitioned ant.
   */
  private generateTransitionName(previousName: string, newRole: AntRole, transitionCount: number): string {
    // Extract base prefix from previous name
    const parts = previousName.split('-');
    const prefix = parts[0].split(' ')[0]; // Get first word/prefix

    // Role indicators
    const roleIndicators: Record<AntRole, string> = {
      [AntRole.FORAGER]: 'F',
      [AntRole.NURSE]: 'N',
      [AntRole.SOLDIER]: 'S',
      [AntRole.ENGINEER]: 'E',
      [AntRole.QUEEN]: 'Q',
    };

    // Generate new name with role indicator and transition count
    const indicator = roleIndicators[newRole];
    const romanNumerals = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X'];
    const numeral = romanNumerals[Math.min(transitionCount - 1, romanNumerals.length - 1)];

    return `${prefix}-${indicator}${numeral}`;
  }

  /**
   * Update physical attributes when changing roles.
   */
  private updatePhysicalAttributes(ant: TransitionableAnt, newRole: AntRole): void {
    // Gradually adjust attributes based on new role
    // These changes are partial - the ant retains some characteristics from before
    switch (newRole) {
      case AntRole.FORAGER:
        ant.speed = ant.baseSpeed * 1.0;
        // Size stays the same
        break;
      case AntRole.NURSE:
        ant.speed = ant.baseSpeed * 0.9;
        break;
      case AntRole.SOLDIER:
        ant.speed = ant.baseSpeed * 1.1;
        // Soldiers become slightly larger over time (not instant)
        ant.size = Math.min(1.3, ant.size * 1.1);
        break;
      case AntRole.ENGINEER:
        ant.speed = ant.baseSpeed * 0.95;
        break;
    }
  }

  /**
   * Get transition history for an ant.
   */
  getTransitionHistory(ant: Ant): PreviousRole[] {
    return ant.previousRoles;
  }

  /**
   * Check if a transition is allowed.
   */
  isTransitionAllowed(fromRole: AntRole, toRole: AntRole): boolean {
    const allowed = ALLOWED_TRANSITIONS.get(fromRole);
    return allowed !== undefined && allowed.includes(toRole);
  }

  /**
   * Force a specific ant to transition (for testing/debugging).
   */
  forceTransition(
    ant: Ant,
    newRole: AntRole,
    context: RoleTransitionContext
  ): boolean {
    if (!this.isTransitionAllowed(ant.role, newRole)) {
      return false;
    }

    this.executeTransition(ant as TransitionableAnt, newRole, context);
    return true;
  }

  getConfig(): RoleTransitionConfig {
    return { ...this.config };
  }

  /**
   * Update config with partial values (for live settings changes).
   */
  updateConfig(partial: Partial<RoleTransitionConfig>): void {
    this.config = { ...this.config, ...partial };
  }
}
